import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Menu, X } from "lucide-react";
import { openEnrollDialog } from "../hooks/useEnroll";
import { useContent } from "../contexts/ContentContext";

const ALL_LINKS = [
  { label: "About", href: "#about", key: "about" },
  { label: "Courses", href: "#courses", key: "courses" },
  { label: "Faculty", href: "#faculty", key: "faculty" },
  { label: "Results", href: "#achievements", key: "achievements" },
  { label: "Testimonials", href: "#testimonials", key: "testimonials" },
  { label: "Contact", href: "#contact", key: "contact" },
];

export default function Header() {
  const { content } = useContent();
  const settings = content?.settings || {};
  const links = ALL_LINKS.filter((l) => content?.[l.key]?.enabled !== false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-black/5 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 lg:h-20 flex items-center justify-between">
        <a href="#top" data-testid="header-logo-link" className="flex items-center gap-3">
          {settings.logo_url && (
            <img src={settings.logo_url} alt={settings.institute_name || "Logo"} className="h-10 lg:h-12 w-auto" />
          )}
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-base lg:text-lg font-semibold tracking-tight text-foreground">
              {settings.short_name || "Sunbright"}
            </span>
            {(settings.tagline ?? "Pharma Academy") && (
              <span className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                {settings.tagline ?? "Pharma Academy"}
              </span>
            )}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="px-3 py-2 text-sm text-foreground/80 hover:text-primary transition-colors rounded-md"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            data-testid="header-enroll-btn"
            onClick={openEnrollDialog}
            className="hidden sm:inline-flex bg-primary text-white hover:bg-[#059669] rounded-md px-5"
          >
            Enroll Now
          </Button>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-md hover:bg-secondary"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-black/5">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="px-3 py-3 text-base text-foreground/90 hover:text-primary border-b border-black/5"
              >
                {l.label}
              </a>
            ))}
            <Button
              data-testid="mobile-enroll-btn"
              onClick={() => {
                setOpen(false);
                openEnrollDialog();
              }}
              className="mt-2 bg-primary text-white hover:bg-[#059669]"
            >
              Enroll Now
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
